import React, { useRef, useState, useEffect } from "react";
import { captureFrame } from "lib/utils/captureFrame";
import { formatTime } from "lib/utils/formatTime";
import { sortByTimeAsc } from "lib/project/dataSorting";
import { getFrameFft } from "lib/api/getFrameFft";
import { useAppStore } from "lib/store/useAppStore";
import SingleImageDownloadBtn from "components/project/SingleImageDownloadBtn";
import FileValueTable from "components/project/FileValueTable";
import FftBarGraph from "components/project/FftBarGraph";
import VideoFrames from "components/project/VideoFrames";
import Switch from "components/project/Switch";
import Waveform from "components/project/WaveFrom";

export default function VideoAnalysisPanel({
  file,
  refs,
  selectedFrame,
  setSelectedFrame,
  t,
}) {
  const showAlert = useAppStore((s) => s.showAlert);

  const videoRef = useRef(null);

  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoFrames, setVideoFrames] = useState([]);
  const [isCompare, setIsCompare] = useState(false);
  const [samplesPerPixel, setSamplesPerPixel] = useState([30, 30]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => setTime(video.currentTime);
    const onLoadedMetadata = () => setDuration(video.duration);
    const onError = (e) => {
      console.warn("video_mp4:", file?.video_mp4);
    };

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("error", onError);

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("error", onError);
    };
  }, [file?.video_mp4]);

  const clickVideoSnapshot = () => {
    const video = videoRef.current;
    if (!video) return;

    const addCapture = () => {
      const imgData = captureFrame(video);

      setVideoFrames((prev) => {
        const exists = prev.some(
          (i) =>
            Math.floor(i.seconds * 100) === Math.floor(video.currentTime * 100),
        );
        if (exists) return prev;

        return sortByTimeAsc([
          ...prev,
          {
            time: formatTime(video.currentTime).pretty,
            timeToFloat: video.currentTime,
            // seconds: video.currentTime,
            src: imgData,
            compact: formatTime(video.currentTime).compact,
          },
        ]);
      });
    };

    if (!video.paused && "requestVideoFrameCallback" in video) {
      video.requestVideoFrameCallback(addCapture);
    } else {
      addCapture();
    }
  };

  const deleteFrame = (idx) => {
    const updated = videoFrames.filter((_, index) => idx !== index);
    setVideoFrames(updated);

    setSelectedFrame((prev) =>
      prev.filter((f) => f?.src !== videoFrames[idx]?.src),
    );
  };

  const handleClickFrame = async (idx) => {
    const clicked = videoFrames[idx];
    if (!clicked) return;

    const existingIndex = selectedFrame.findIndex(
      (f) => f?.src === clicked.src,
    );

    if (existingIndex !== -1) {
      setSelectedFrame((prev) => {
        const updated = [...prev];
        updated[existingIndex] = null;
        return updated;
      });
      return;
    }

    const { data, success, message, messageKey, messageParams } =
      await getFrameFft(file?.upload_id, clicked?.time);

    if (!success) {
      showAlert({
        text: message || "",
        messageKey,
        messageParams,
        success: success,
        autoClose: true,
      });
      return;
    }

    const enrichedFrame = {
      ...clicked,
      decibel: data.detected_decibel,
      max_decibel: data.max_decibel,
      peak_value: data.peak_value,
      fft_array: data.fft_array,
      peak_hz: data.peak_hz,
      spectrogram_img: data.spectrogram_img,
      distance: data.distance_value,
    };

    setSelectedFrame((prev) => {
      let updated = prev.length === 2 ? [...prev] : [null, null];
      if (!updated[0]) updated[0] = enrichedFrame;
      else if (!updated[1]) updated[1] = enrichedFrame;
      else updated[1] = enrichedFrame;
      return updated;
    });
  };

  const STEP = 7.5;
  const MAX = 150;
  const MIN = STEP;

  const handleIncrease = (index) => {
    setSamplesPerPixel((prev) => {
      const temp = [...prev];
      temp[index] = Math.min(temp[index] + STEP, MAX);
      return temp;
    });
  };

  const handleDecrease = (index) => {
    setSamplesPerPixel((prev) => {
      const temp = [...prev];
      temp[index] = Math.max(temp[index] - STEP, MIN);
      return temp;
    });
  };

  const downloadVideo = () => {
    const src = file?.video_mp4;
    if (!src)
      return showAlert({
        success: false,
        autoClose: true,
        text: "",
      });

    const url = src.includes("&download=1") ? src : `${src}&download=1`;
    window.open(url, "_self");
  };

  const downloadWav = () => {
    const src = file?.video_wav;
    if (!src)
      return showAlert({
        success: false,
        autoClose: true,
        text: "",
      });

    const url = src.includes("&download=1") ? src : `${src}&download=1`;
    window.open(url, "_self");
  };
      
  return (
    <div className="streamPanel">
      <div>
        <div>
          <div className="imgDownloadBtnBox">
            <p className="imgTitleP">
              {t("videoFileIndex.subTitle.originalVideoPlayer")}
            </p>
            <button onClick={downloadVideo} className="btn-bg-blue">
              {t("videoFileIndex.buttons.mp4Download")}
            </button>

            <button onClick={downloadWav} className="btn-bg-blue">
              {t("videoFileIndex.buttons.wavDownload")}
            </button>
            <button
              title="frame capture"
              onClick={clickVideoSnapshot}
              className="btn-bg-blue"
            >
              {t("videoFileIndex.buttons.extractFrame")}
            </button>
          </div>
          <video
            controls
            ref={videoRef}
            src={file?.video_mp4 || undefined}
            playsInline
            crossOrigin="use-credentials"
          />
        </div>
        <VideoFrames
          videoFrames={videoFrames}
          deleteFrame={deleteFrame}
          handleClickFrame={handleClickFrame}
          selectedFrame={selectedFrame}
        />
      </div>

      {selectedFrame[0] ? (
        <>
          <hr />
          <Switch
            value={isCompare}
            setValue={setIsCompare}
            labels={[
              t("videoFileIndex.modes.single"),
              t("videoFileIndex.modes.compare"),
            ]}
          />
        </>
      ) : (
        <div></div>
      )}
      <div className={!isCompare ? "singleMode" : "compareMode"}>
        {selectedFrame[0] && (
          <div>
            <div>
              <div>
                <div className="imgDownloadBtnBox">
                  <p className="imgTitleP">
                    {t("videoFileIndex.subTitle.ultrasonicImage")} {1}
                  </p>
                  <time>{selectedFrame[0]?.time}</time>
                  <SingleImageDownloadBtn
                    captureTargetRef={refs.frame_ref_1}
                    fileName={`${file?.clean_file_name}_visual`}
                    t={t("videoFileIndex.buttons.singleImageDownload")}
                  />
                </div>
                <img
                  style={{ maxWidth: "650px" }}
                  ref={refs.frame_ref_1}
                  src={selectedFrame[0]?.src}
                  alt={`frame`}
                />
              </div>
              <div>
                <p className="imgTitleP">
                  {t("videoFileIndex.subTitle.MeasurementValue")} {file?.volume}
                </p>
                <FileValueTable
                  type={"ultrasonic"}
                  parsedData={selectedFrame[0]}
                  t={t}
                />
              </div>
            </div>
            <div>
              <div>
                <div className="imgDownloadBtnBox">
                  <p className="imgTitleP">
                    {t("videoFileIndex.subTitle.fftGraph")}
                  </p>
                  <SingleImageDownloadBtn
                    captureTargetRef={refs.graph_ref_1}
                    fileName={`${file?.clean_file_name}_fftgraph`}
                    t={t("videoFileIndex.buttons.singleImageDownload")}
                  />
                </div>
                <FftBarGraph
                  ref={refs.graph_ref_1}
                  data={selectedFrame[0]?.fft_array}
                />
              </div>
              <div>
                <div className="imgDownloadBtnBox">
                  <p className="imgTitleP">
                    {t("videoFileIndex.subTitle.spectrogram")}
                  </p>
                  <SingleImageDownloadBtn
                    captureTargetRef={refs.spectrogram_ref_1}
                    fileName={`${file?.clean_file_name}_spectrogram`}
                    t={t("videoFileIndex.buttons.singleImageDownload")}
                  />
                </div>
                <img
                  ref={refs.spectrogram_ref_1}
                  src={selectedFrame[0]?.spectrogram_img}
                  alt={`spectrogram`}
                />
              </div>
              <div>
                <div className="imgDownloadBtnBox">
                  <p className="imgTitleP">
                    {t("videoFileIndex.subTitle.waveform")}{" "}
                    {(samplesPerPixel[0] * 0.00667).toFixed(2)}s (
                    {640 * samplesPerPixel[0] >= 1000
                      ? (640 * samplesPerPixel[0]).toLocaleString()
                      : 640 * samplesPerPixel[0]}{" "}
                    sample @ 96kHz)
                  </p>
                  <div className="samplesPerPixel">
                    <button onClick={() => handleIncrease(0)}>+</button>
                    <button onClick={() => handleDecrease(0)}>-</button>
                  </div>
                  <SingleImageDownloadBtn
                    captureTargetRef={refs.waveform_ref_1}
                    fileName={`${file?.clean_file_name}_waveForm`}
                    t={t("videoFileIndex.buttons.singleImageDownload")}
                  />
                </div>
                <Waveform
                  samplesPerPixel={samplesPerPixel[0]}
                  pcmDataUrl={file?.video_decoded_wav_f32}
                  time={selectedFrame?.timeToFloat}
                  captureRef={refs.waveform_ref_1}
                />
              </div>
            </div>
          </div>
        )}
        {!isCompare && selectedFrame[0] && <hr />}
        {selectedFrame[1] && (
          <div>
            <div>
              <div>
                <div className="imgDownloadBtnBox">
                  <p className="imgTitleP">
                    {t("videoFileIndex.subTitle.ultrasonicImage")} {2}
                  </p>
                  <time>{selectedFrame[1]?.time}</time>
                  <SingleImageDownloadBtn
                    captureTargetRef={refs.frame_ref_2}
                    fileName={`${file?.clean_file_name}_visual`}
                    t={t("videoFileIndex.buttons.singleImageDownload")}
                  />
                </div>
                <img
                  style={{ maxWidth: "650px" }}
                  ref={refs.frame_ref_2}
                  src={selectedFrame[1]?.src}
                  alt={`frame`}
                />
              </div>
              <div>
                <p className="imgTitleP">
                  {t("videoFileIndex.subTitle.MeasurementValue")} {file?.volume}
                </p>
                <FileValueTable
                  type={"ultrasonic"}
                  parsedData={selectedFrame[1]}
                  t={t}
                />
              </div>
            </div>
            <div>
              <div>
                <div className="imgDownloadBtnBox">
                  <p className="imgTitleP">
                    {t("videoFileIndex.subTitle.fftGraph")}
                  </p>
                  <SingleImageDownloadBtn
                    captureTargetRef={refs.graph_ref_2}
                    fileName={`${file?.clean_file_name}_fftgraph`}
                    t={t("videoFileIndex.buttons.singleImageDownload")}
                  />
                </div>
                <FftBarGraph
                  ref={refs.graph_ref_2}
                  data={selectedFrame[1]?.fft_array}
                />
              </div>
              <div>
                <div className="imgDownloadBtnBox">
                  <p className="imgTitleP">
                    {t("videoFileIndex.subTitle.spectrogram")}
                  </p>
                  <SingleImageDownloadBtn
                    captureTargetRef={refs.spectrogram_ref_2}
                    fileName={`${file?.clean_file_name}_spectrogram`}
                    t={t("videoFileIndex.buttons.singleImageDownload")}
                  />
                </div>
                <img
                  ref={refs.spectrogram_ref_2}
                  src={selectedFrame[1]?.spectrogram_img}
                  alt={`spectrogram`}
                />
              </div>
              <div>
                <div className="imgDownloadBtnBox">
                  <p className="imgTitleP">
                    {t("videoFileIndex.subTitle.waveform")}
                    {(samplesPerPixel[1] * 0.00667).toFixed(2)}s (
                    {640 * samplesPerPixel[1] >= 1000
                      ? (640 * samplesPerPixel[1]).toLocaleString()
                      : 640 * samplesPerPixel[1]}{" "}
                    sample @ 96kHz)
                  </p>
                  <div className="samplesPerPixel">
                    <button onClick={() => handleIncrease(1)}>+</button>
                    <button onClick={() => handleDecrease(1)}>-</button>
                  </div>
                  <SingleImageDownloadBtn
                    captureTargetRef={refs.waveform_ref_2}
                    fileName={`${file?.clean_file_name}_waveForm`}
                    t={t("videoFileIndex.buttons.singleImageDownload")}
                  />
                </div>
                <Waveform
                  pcmDataUrl={file?.video_decoded_wav_f32}
                  time={selectedFrame[1]?.timeToFloat}
                  captureRef={refs.waveform_ref_2}
                  samplesPerPixel={samplesPerPixel[1]}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
